/*
  HME — Search takeover.

  Turns every persistent header search bar into a trigger that opens
  one shared takeover panel, and keeps the panel's suggestions visible
  only while the field is empty.

  PROGRESSIVE ENHANCEMENT, ON PURPOSE.
  The header bars are real inputs inside real <form> elements, so with
  this file absent or broken they still search normally. Nothing here
  runs until the panel is found in the DOM.

  ONE INPUT, NOT TWO.
  The panel renders the same hme-header-search-bar snippet with its own
  input_id, so it owns a genuine <predictive-search> element. The header
  bars hand their typed value over on open and then sit covered, which
  is why there is no ongoing two-way sync to get wrong.
*/
(function () {
  var panel = document.getElementById('HmeSearchTakeover');
  if (!panel) return;

  /*
    Hoist the panel to <body>.

    It is authored inside the header section so a merchant edits its copy
    where they expect to, but that section sits in a stacking context of
    its own, and the announcement bar's section paints above it. The panel
    rendered underneath the announcement bar no matter how high its
    z-index went, because z-index only competes inside its own context.

    Moving it to a direct child of <body> takes it out of every section's
    context, so position: fixed and z-index behave the way the CSS reads.
    Guarded against the theme editor re-rendering the section and leaving
    a second copy behind.
  */
  if (panel.parentElement !== document.body) {
    var stale = document.querySelectorAll('#HmeSearchTakeover');
    for (var i = 0; i < stale.length; i++) {
      if (stale[i] !== panel && stale[i].parentElement === document.body) stale[i].remove();
    }
    document.body.appendChild(panel);
  }

  var field = panel.querySelector('.hme-searchbar__input');
  var suggest = panel.querySelector('[data-takeover-suggest]');
  var backdrop = panel.querySelector('.hme-takeover__backdrop');
  var closeBtn = panel.querySelector('.hme-takeover__close');
  if (!field) return;

  var lastTrigger = null;

  // Closing returns focus to the bar that opened the panel, and that bar
  // opens the panel on focus. Without this guard the two chase each other
  // and the panel can never be dismissed. Found by testing Escape.
  var restoringFocus = false;

  function syncSuggest() {
    if (!suggest) return;
    // Two characters is the same floor Shopify's predictive search uses
    // before it will return anything, so the swap lines up with results
    // actually arriving rather than flickering on the first keystroke.
    suggest.hidden = field.value.trim().length >= 2;
  }

  // The announcement bar stays visible and usable while the panel is
  // open, so the panel has to start where that bar ends. Measured per
  // open rather than hardcoded: its height moves with the viewport, and
  // it scrolls away on a long page, in which case the offset is 0.
  function applyTopOffset() {
    var bar = document.querySelector('.hme-topbar');
    var offset = 0;
    if (bar) {
      var rect = bar.getBoundingClientRect();
      if (rect.height > 0) offset = Math.max(0, Math.round(rect.bottom));
    }
    panel.style.setProperty('--hme-takeover-top', offset + 'px');
  }

  function open(trigger, seedValue) {
    if (restoringFocus) return;
    if (panel.classList.contains('is-open')) return;
    lastTrigger = trigger || null;
    applyTopOffset();
    panel.hidden = false;
    // Force a reflow so the transition runs from its start state
    // instead of the browser collapsing both frames into one.
    void panel.offsetWidth;
    panel.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (seedValue) {
      field.value = seedValue;
      field.dispatchEvent(new Event('input', { bubbles: true }));
    }
    syncSuggest();
    window.setTimeout(function () { field.focus(); }, 60);
  }

  function close() {
    if (!panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    document.body.style.overflow = '';
    field.value = '';
    syncSuggest();

    // Wait out the transition before hiding, or the panel vanishes
    // instead of sliding away.
    window.setTimeout(function () {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    }, 320);

    // Send focus back where it came from, so keyboard users are not
    // dropped at the top of the document. Guarded, see restoringFocus above.
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      restoringFocus = true;
      lastTrigger.focus({ preventScroll: true });
      window.setTimeout(function () { restoringFocus = false; }, 0);
    }
  }

  // Every header bar OUTSIDE the panel becomes a trigger.
  document.querySelectorAll('.hme-searchbar__input').forEach(function (input) {
    if (panel.contains(input)) return;

    input.addEventListener('focus', function () {
      var seed = input.value;
      input.blur();
      open(input, seed);
    });

    // Touch devices fire click without a preceding focus often enough
    // that focus alone is not reliable here.
    input.addEventListener('click', function (event) {
      event.preventDefault();
      open(input, input.value);
    });
  });

  // The submit button on a covered bar should open the panel too rather
  // than firing a search against an empty field.
  document.querySelectorAll('.hme-searchbar__submit').forEach(function (button) {
    if (panel.contains(button)) return;
    button.addEventListener('click', function (event) {
      var input = button.closest('.hme-searchbar__form')?.querySelector('.hme-searchbar__input');
      if (input && !input.value.trim()) {
        event.preventDefault();
        open(input, '');
      }
    });
  });

  field.addEventListener('input', syncSuggest);

  panel.querySelectorAll('.hme-takeover__chip').forEach(function (chip) {
    chip.addEventListener('click', function () {
      field.value = chip.dataset.term || chip.textContent.trim();
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.focus();
      syncSuggest();
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', close);
  if (backdrop) backdrop.addEventListener('click', close);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && panel.classList.contains('is-open')) close();
  });

  // A resize while open changes the bar's height, and the panel would
  // otherwise keep the offset it was opened with.
  window.addEventListener('resize', function () {
    if (panel.classList.contains('is-open')) applyTopOffset();
  });

  // Keep focus inside the panel while it is open. Without this, tabbing
  // walks into the page behind the backdrop, which is invisible and
  // unusable.
  panel.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;
    var focusable = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
